<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import UserMenuDropdown from '@/components/Head/UserMenuDropdown.vue'
import { userModule } from '@/store/user/module'
import { instructorModule } from '@/store/instructor/module'
import { setCookie } from '@/utils'
import type { ComponentPublicInstance } from 'vue'
import { upgradeModule } from '@/store/upgrade/module'

const router = useRouter()

const showDropdown = ref(false)
const isLoggingOut = ref(false)

const dropdownBtn = useTemplateRef<HTMLElement>('dropdownBtn')
const dropdown = useTemplateRef<ComponentPublicInstance>('dropdown')

const initial = computed(() => {
    return userModule.getters.userInitial()
})

const firstName = computed(() => {
    return userModule.state.user?.firstName || userModule.state.user?.email.split('@')[0]
})

const lastActiveApp = computed(() => {
    return userModule.state.user?.webConfig?.lastActiveApp
})

onMounted(async () => {
    await userModule.actions.fetchUserData()
    if (lastActiveApp.value === 'teach') {
        const teachURL = import.meta.env.VUE_APP_TEACH_URL
        const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
        const sessionToken = userModule.getters.getSessionToken()
        if (teachURL && sessionToken && sessionCookieName) {
            setCookie({ name: sessionCookieName, value: sessionToken })
            window.location.href = teachURL
        }
    } else if (userModule.state.user && !userModule.state.updatingWebConfig) {
        await userModule.actions.updateWebConfig({
            lastActiveApp: 'study',
        })
    }

    document.addEventListener('click', clickListener)

    await instructorModule.actions.fetchCurrentUserOrgInstructors()
})

onBeforeUnmount(() => {
    document.removeEventListener('click', clickListener)
})

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const clickListener = (e: MouseEvent) => {
    if (showDropdown.value) {
        const targetEl = e.target as HTMLElement
        const dropdownEl = dropdown.value?.$el as HTMLElement | undefined
        const dropdownBtnEl = dropdownBtn.value

        if (
            !dropdownBtnEl?.contains(targetEl)
            && !dropdownEl?.contains(targetEl)
            && document.body.contains(targetEl)
        ) {
            showDropdown.value = false
        }
    }
}

const clickDropdownOption = async (option: string) => {
    if (option === 'signOut' && !isLoggingOut.value) {
        isLoggingOut.value = true
        showDropdown.value = false
        await userModule.actions.signOut()
        router.push({ name: 'sign-in' })
        isLoggingOut.value = false
    }
}
</script>

<template>
    <div class="user-menu">
        <div
            ref="dropdownBtn"
            v-dark
            class="user-menu__button"
            role="button"
            :aria-expanded="showDropdown ? 'true' : 'false'"
            :aria-label="`${firstName} user dropdown`"
            tabindex="0"
            @click="showDropdown = !showDropdown"
            @keydown.enter="showDropdown = !showDropdown"
            @mousedown.prevent
        >
            <div aria-hidden="true" class="user-menu__initial">
                <span>{{ initial }}</span>
            </div>
            <div aria-hidden="true" class="user-menu__name">
                {{ firstName }}
            </div>
        </div>
        <UserMenuDropdown
            v-if="showDropdown"
            ref="dropdown"
            v-dark
            class="user-menu__dropdown"
            @close="showDropdown = false"
            @clickShowUpgrade="openUpgradeModal(); showDropdown = false"
            @clickDropdownOption="clickDropdownOption"
        />
    </div>
</template>

<style lang="scss" scoped>
.user-menu {
    position: relative;
    padding: 8px 0;
    z-index: 1001; // just above side panel

    &__button {
        display: flex;
        padding: 5px 8px 5px 6px;
        cursor: pointer;
        user-select: none;
        border-radius: 6px;
        border: 1px solid transparent;

        &:hover {
            background: $charcoal;
        }

        &:focus {
            background: $charcoal;
            border: 1px solid $banana-bread;
            outline: none;
        }

        &--dark {
            &:hover,
            &:focus {
                background: $brand-black;
            }
        }
    }

    &__initial {
        width: 26px;
        height: 26px;
        margin-right: 12px;
        background: linear-gradient(216.65deg, $beach-sand 0%, $cream-puff 100%);
        color: $flat-brown;
        font-size: 16px;
        font-weight: 600;
        line-height: 1px;
        border-radius: 26px;
        text-transform: uppercase;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__name {
        font-size: 15px;
        line-height: 26px;
        text-transform: capitalize;
        max-width: 120px;
        text-overflow: ellipsis;
        overflow: hidden;

        @include breakpoint(grizzly-bear) {
            max-width: 90px;
        }
    }

    &__dropdown {
        position: absolute;
        top: 54px;
        left: -57px;
        background: $white;
        box-shadow: 0 6px 22px 0 rgba($brand-black, 0.2);
        width: 220px;
        border-radius: 0 0 8px 8px;

        &--dark {
            background: $charcoal;
            box-shadow: 0 6px 22px 0 rgba($jet, 0.6);
        }

        @include breakpoint(grizzly-bear) {
            left: -70px;
        }
    }
}
</style>
